import { useSyncExternalStore } from "react";
import {
  syncCreateCategory,
  syncUpdateCategory,
  syncDeleteCategory,
  syncImportCategories,
} from "../../lib/api/db-sync";

// ---------------------------------------------------------------------------
// Color palette — predefined Tailwind classes for custom categories
// ---------------------------------------------------------------------------

export interface ColorPalette {
  readonly id: string;
  readonly label: string;
  readonly dot: string;
  readonly bar: string;
  readonly block: string;
  readonly badge: string;
  readonly pill: string;
  readonly colors: string;
  /** Raw CSS color for inline styles (e.g. slot preview) */
  readonly css: string;
}

const PALETTE: readonly ColorPalette[] = [
  {
    id: "purple",
    label: "Violet",
    dot: "bg-purple-500",
    bar: "bg-purple-500",
    block: "bg-purple-100 dark:bg-purple-500/20 border-l-purple-500 text-purple-700 dark:text-purple-200",
    badge: "text-purple-700 bg-purple-50 dark:text-purple-300 dark:bg-purple-900/20",
    pill: "bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-200",
    colors: "bg-purple-100 border-purple-300 text-purple-800",
    css: "#a855f7",
  },
  {
    id: "amber",
    label: "Ambre",
    dot: "bg-amber-500",
    bar: "bg-amber-500",
    block: "bg-amber-100 dark:bg-amber-500/20 border-l-amber-500 text-amber-700 dark:text-amber-200",
    badge: "text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/20",
    pill: "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-200",
    colors: "bg-amber-100 border-amber-300 text-amber-800",
    css: "#f59e0b",
  },
  {
    id: "blue",
    label: "Bleu",
    dot: "bg-blue-500",
    bar: "bg-blue-500",
    block: "bg-blue-100 dark:bg-blue-500/20 border-l-blue-500 text-blue-700 dark:text-blue-200",
    badge: "text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/20",
    pill: "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-200",
    colors: "bg-blue-100 border-blue-300 text-blue-800",
    css: "#3b82f6",
  },
  {
    id: "emerald",
    label: "Émeraude",
    dot: "bg-emerald-500",
    bar: "bg-emerald-500",
    block: "bg-emerald-100 dark:bg-emerald-500/20 border-l-emerald-500 text-emerald-700 dark:text-emerald-200",
    badge: "text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/20",
    pill: "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-200",
    colors: "bg-emerald-100 border-emerald-300 text-emerald-800",
    css: "#10b981",
  },
  {
    id: "rose",
    label: "Rose",
    dot: "bg-rose-500",
    bar: "bg-rose-500",
    block: "bg-rose-100 dark:bg-rose-500/20 border-l-rose-500 text-rose-700 dark:text-rose-200",
    badge: "text-rose-700 bg-rose-50 dark:text-rose-300 dark:bg-rose-900/20",
    pill: "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-200",
    colors: "bg-rose-100 border-rose-300 text-rose-800",
    css: "#f43f5e",
  },
  {
    id: "cyan",
    label: "Cyan",
    dot: "bg-cyan-500",
    bar: "bg-cyan-500",
    block: "bg-cyan-100 dark:bg-cyan-500/20 border-l-cyan-500 text-cyan-700 dark:text-cyan-200",
    badge: "text-cyan-700 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-900/20",
    pill: "bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-200",
    colors: "bg-cyan-100 border-cyan-300 text-cyan-800",
    css: "#06b6d4",
  },
  {
    id: "orange",
    label: "Orange",
    dot: "bg-orange-500",
    bar: "bg-orange-500",
    block: "bg-orange-100 dark:bg-orange-500/20 border-l-orange-500 text-orange-700 dark:text-orange-200",
    badge: "text-orange-700 bg-orange-50 dark:text-orange-300 dark:bg-orange-900/20",
    pill: "bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-200",
    colors: "bg-orange-100 border-orange-300 text-orange-800",
    css: "#f97316",
  },
  {
    id: "pink",
    label: "Rose vif",
    dot: "bg-pink-500",
    bar: "bg-pink-500",
    block: "bg-pink-100 dark:bg-pink-500/20 border-l-pink-500 text-pink-700 dark:text-pink-200",
    badge: "text-pink-700 bg-pink-50 dark:text-pink-300 dark:bg-pink-900/20",
    pill: "bg-pink-100 dark:bg-pink-500/20 text-pink-700 dark:text-pink-200",
    colors: "bg-pink-100 border-pink-300 text-pink-800",
    css: "#ec4899",
  },
  {
    id: "indigo",
    label: "Indigo",
    dot: "bg-indigo-500",
    bar: "bg-indigo-500",
    block: "bg-indigo-100 dark:bg-indigo-500/20 border-l-indigo-500 text-indigo-700 dark:text-indigo-200",
    badge: "text-indigo-700 bg-indigo-50 dark:text-indigo-300 dark:bg-indigo-900/20",
    pill: "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-200",
    colors: "bg-indigo-100 border-indigo-300 text-indigo-800",
    css: "#6366f1",
  },
  {
    id: "teal",
    label: "Turquoise",
    dot: "bg-teal-500",
    bar: "bg-teal-500",
    block: "bg-teal-100 dark:bg-teal-500/20 border-l-teal-500 text-teal-700 dark:text-teal-200",
    badge: "text-teal-700 bg-teal-50 dark:text-teal-300 dark:bg-teal-900/20",
    pill: "bg-teal-100 dark:bg-teal-500/20 text-teal-700 dark:text-teal-200",
    colors: "bg-teal-100 border-teal-300 text-teal-800",
    css: "#14b8a6",
  },
  {
    id: "red",
    label: "Rouge",
    dot: "bg-red-500",
    bar: "bg-red-500",
    block: "bg-red-100 dark:bg-red-500/20 border-l-red-500 text-red-700 dark:text-red-200",
    badge: "text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/20",
    pill: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-200",
    colors: "bg-red-100 border-red-300 text-red-800",
    css: "#ef4444",
  },
  {
    id: "lime",
    label: "Citron vert",
    dot: "bg-lime-500",
    bar: "bg-lime-500",
    block: "bg-lime-100 dark:bg-lime-500/20 border-l-lime-500 text-lime-700 dark:text-lime-200",
    badge: "text-lime-700 bg-lime-50 dark:text-lime-300 dark:bg-lime-900/20",
    pill: "bg-lime-100 dark:bg-lime-500/20 text-lime-700 dark:text-lime-200",
    colors: "bg-lime-100 border-lime-300 text-lime-800",
    css: "#84cc16",
  },
] as const;

const paletteMap = new Map(PALETTE.map((p) => [p.id, p]));

export function getPalette(): readonly ColorPalette[] {
  return PALETTE;
}

export function getPaletteById(id: string): ColorPalette | undefined {
  return paletteMap.get(id);
}

// ---------------------------------------------------------------------------
// Custom category definition
// ---------------------------------------------------------------------------

export interface CategoryOptions {
  readonly goalHours: number | null; // weekly goal (e.g. 35h for pro)
  readonly showInWorkHours: boolean; // show in "Heures planifiées" widget
  readonly hidden?: boolean; // soft-delete for built-in categories
}

export interface CustomCategory {
  readonly id: string; // slug used as Category value
  readonly label: string;
  readonly colorId: string; // references PALETTE[].id
  readonly options?: CategoryOptions;
}

// ---------------------------------------------------------------------------
// Default (built-in) categories
// ---------------------------------------------------------------------------

interface BuiltInCategory {
  readonly id: string;
  readonly label: string;
  readonly colorId: string;
  readonly options: CategoryOptions;
}

const BUILT_IN: readonly BuiltInCategory[] = [
  { id: "spiritual", label: "Spirituel", colorId: "purple", options: { goalHours: null, showInWorkHours: true } },
  { id: "personal", label: "Personnel", colorId: "amber", options: { goalHours: null, showInWorkHours: true } },
  { id: "professional", label: "Professionnel", colorId: "blue", options: { goalHours: 35, showInWorkHours: true } },
];

// ---------------------------------------------------------------------------
// Store state
// ---------------------------------------------------------------------------

const STORAGE_KEY = "palnia_custom_categories";
const SETTINGS_KEY = "palnia_category_settings";

interface BuiltInOverride {
  readonly label?: string;
  readonly colorId?: string;
}

interface CategorySettings {
  readonly workHoursTitle: string;
  readonly categoryOptions: Record<string, CategoryOptions>;
  readonly builtInOverrides: Record<string, BuiltInOverride>;
}

const DEFAULT_SETTINGS: CategorySettings = {
  workHoursTitle: "Heures planifiées — semaine en cours",
  categoryOptions: {},
  builtInOverrides: {},
};

function loadSettings(): CategorySettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<CategorySettings>;
    return {
      workHoursTitle: typeof parsed.workHoursTitle === "string" ? parsed.workHoursTitle : DEFAULT_SETTINGS.workHoursTitle,
      categoryOptions: (parsed.categoryOptions && typeof parsed.categoryOptions === "object") ? parsed.categoryOptions : {},
      builtInOverrides: (parsed.builtInOverrides && typeof parsed.builtInOverrides === "object") ? parsed.builtInOverrides as Record<string, BuiltInOverride> : {},
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function persistSettings() {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

let settings = loadSettings();

function loadCustomCategories(): CustomCategory[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (c): c is CustomCategory =>
        typeof c === "object" &&
        c !== null &&
        typeof (c as Record<string, unknown>).id === "string" &&
        typeof (c as Record<string, unknown>).label === "string" &&
        typeof (c as Record<string, unknown>).colorId === "string",
    );
  } catch {
    return [];
  }
}

function persist(categories: CustomCategory[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
}

let customCategories = loadCustomCategories();

// Server category IDs mapped by name (slug) for sync
let serverCategoryIds: Record<string, string> = {};

const SERVER_IDS_KEY = "palnia_category_server_ids";

function loadServerIds(): Record<string, string> {
  try {
    const raw = localStorage.getItem(SERVER_IDS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persistServerIds() {
  localStorage.setItem(SERVER_IDS_KEY, JSON.stringify(serverCategoryIds));
}

serverCategoryIds = loadServerIds();

// ---------------------------------------------------------------------------
// Derived maps (rebuilt on every change)
// ---------------------------------------------------------------------------

interface CategoryMaps {
  readonly categories: readonly string[];
  readonly labels: Record<string, string>;
  readonly dotColors: Record<string, string>;
  readonly barColors: Record<string, string>;
  readonly blockColors: Record<string, string>;
  readonly badgeColors: Record<string, string>;
  readonly pillColors: Record<string, string>;
  readonly tagColors: Record<string, string>;
  readonly cssColors: Record<string, string>;
  readonly categoryOptions: Record<string, CategoryOptions>;
  readonly workHoursTitle: string;
}

const DEFAULT_OPTIONS: CategoryOptions = { goalHours: null, showInWorkHours: true };

function buildMaps(): CategoryMaps {
  const all = [...BUILT_IN, ...customCategories];
  const categories: string[] = [];
  const labels: Record<string, string> = {};
  const dotColors: Record<string, string> = {};
  const barColors: Record<string, string> = {};
  const blockColors: Record<string, string> = {};
  const badgeColors: Record<string, string> = {};
  const pillColors: Record<string, string> = {};
  const tagColors: Record<string, string> = {};
  const cssColors: Record<string, string> = {};
  const categoryOptions: Record<string, CategoryOptions> = {};

  for (const cat of all) {
    const override = settings.categoryOptions[cat.id];
    const base = ("options" in cat && cat.options) ? cat.options : DEFAULT_OPTIONS;
    const opts = override ?? base;

    // Skip hidden categories
    if (opts.hidden) continue;

    const builtInOverride = settings.builtInOverrides[cat.id];
    const effectiveColorId = builtInOverride?.colorId ?? cat.colorId;
    const effectiveLabel = builtInOverride?.label ?? cat.label;

    const palette = paletteMap.get(effectiveColorId) ?? PALETTE[0];
    categories.push(cat.id);
    labels[cat.id] = effectiveLabel;
    dotColors[cat.id] = palette.dot;
    barColors[cat.id] = palette.bar;
    blockColors[cat.id] = palette.block;
    badgeColors[cat.id] = palette.badge;
    pillColors[cat.id] = palette.pill;
    tagColors[cat.id] = palette.colors;
    cssColors[cat.id] = palette.css;

    categoryOptions[cat.id] = opts;
  }

  return {
    categories, labels, dotColors, barColors, blockColors,
    badgeColors, pillColors, tagColors, cssColors, categoryOptions,
    workHoursTitle: settings.workHoursTitle,
  };
}

let maps = buildMaps();

// ---------------------------------------------------------------------------
// Reactive store (subscribe / getSnapshot pattern)
// ---------------------------------------------------------------------------

const listeners = new Set<() => void>();

function emit() {
  maps = buildMaps();
  for (const fn of listeners) fn();
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getSnapshot(): CategoryMaps {
  return maps;
}

// ---------------------------------------------------------------------------
// Hook for React components
// ---------------------------------------------------------------------------

export function useCategories(): CategoryMaps {
  return useSyncExternalStore(subscribe, getSnapshot);
}

// ---------------------------------------------------------------------------
// Server sync
// ---------------------------------------------------------------------------

interface ServerCategory {
  readonly id: string;
  readonly name: string;
  readonly label: string;
  readonly colorId: string;
  readonly goalHours: number | null;
  readonly showInWorkHours: boolean;
  readonly isDefault: boolean;
  readonly hidden: boolean;
}

/**
 * Reçoit les catégories du serveur et les fusionne avec l'état local.
 * Les catégories custom locales non présentes sur le serveur sont poussées via import.
 */
export function resetFromServer(rawCategories: unknown[]): void {
  const serverCats = (rawCategories as ServerCategory[]).filter(
    (c) => typeof c === "object" && c !== null && typeof c.name === "string",
  );

  // Mapper les IDs serveur par nom
  const newServerIds: Record<string, string> = {};
  for (const c of serverCats) {
    newServerIds[c.name] = c.id;
  }
  serverCategoryIds = newServerIds;
  persistServerIds();

  // Reconstruire les catégories custom depuis le serveur
  const serverNames = new Set(serverCats.map((c) => c.name));
  const builtInNames = new Set(BUILT_IN.map((b) => b.id));

  // Catégories custom locales manquantes sur le serveur → import
  const localOnly = customCategories.filter(
    (c) => !serverNames.has(c.id) && !builtInNames.has(c.id),
  );
  if (localOnly.length > 0) {
    const toImport = localOnly.map((c) => {
      const opts = settings.categoryOptions[c.id];
      return {
        name: c.id,
        label: c.label,
        colorId: c.colorId,
        goalHours: opts?.goalHours ?? null,
        showInWorkHours: opts?.showInWorkHours ?? true,
        hidden: opts?.hidden ?? false,
      };
    });
    syncImportCategories(toImport);
  }

  // Remplacer les custom par celles du serveur (non-default)
  customCategories = serverCats
    .filter((c) => !builtInNames.has(c.name))
    .map((c) => ({ id: c.name, label: c.label, colorId: c.colorId }));
  persist(customCategories);

  // Synchroniser les settings (options + overrides) depuis le serveur
  const newOptions: Record<string, CategoryOptions> = {};
  const newOverrides: Record<string, BuiltInOverride> = {};

  for (const c of serverCats) {
    const opts: CategoryOptions = {
      goalHours: c.goalHours,
      showInWorkHours: c.showInWorkHours,
      hidden: c.hidden,
    };

    if (builtInNames.has(c.name)) {
      // C'est un built-in — vérifier si le label/couleur a changé
      const builtIn = BUILT_IN.find((b) => b.id === c.name);
      if (builtIn && (c.label !== builtIn.label || c.colorId !== builtIn.colorId)) {
        newOverrides[c.name] = { label: c.label, colorId: c.colorId };
      }
      // Stocker les options si différentes du défaut
      if (opts.goalHours !== builtIn?.options.goalHours || !opts.showInWorkHours || opts.hidden) {
        newOptions[c.name] = opts;
      }
    } else {
      // Catégorie custom — stocker les options si non-default
      if (opts.goalHours !== null || !opts.showInWorkHours || opts.hidden) {
        newOptions[c.name] = opts;
      }
    }
  }

  settings = {
    ...settings,
    categoryOptions: newOptions,
    builtInOverrides: newOverrides,
  };
  persistSettings();
  emit();
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function slugify(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function addCategory(label: string, colorId: string): string | null {
  const id = slugify(label);
  if (!id) return null;
  const existing = [...BUILT_IN.map((b) => b.id), ...customCategories.map((c) => c.id)];
  if (existing.includes(id)) return null;
  customCategories = [...customCategories, { id, label, colorId }];
  persist(customCategories);
  emit();
  syncCreateCategory({ name: id, label, colorId }, (serverId) => {
    serverCategoryIds[id] = serverId;
    persistServerIds();
  });
  return id;
}

export function removeCategory(id: string): boolean {
  const isBuiltIn = BUILT_IN.some((b) => b.id === id);
  const serverId = serverCategoryIds[id];
  if (isBuiltIn) {
    // Soft-delete: mark as hidden
    const current = settings.categoryOptions[id] ?? DEFAULT_OPTIONS;
    setCategoryOptions(id, { ...current, hidden: true });
    if (serverId) syncUpdateCategory(serverId, { hidden: true });
    return true;
  }
  const before = customCategories.length;
  customCategories = customCategories.filter((c) => c.id !== id);
  if (customCategories.length === before) return false;
  persist(customCategories);
  emit();
  if (serverId) syncDeleteCategory(serverId);
  return true;
}

export function restoreCategory(id: string): boolean {
  const isBuiltIn = BUILT_IN.some((b) => b.id === id);
  if (!isBuiltIn) return false;
  const current = settings.categoryOptions[id] ?? DEFAULT_OPTIONS;
  setCategoryOptions(id, { ...current, hidden: false });
  const serverId = serverCategoryIds[id];
  if (serverId) syncUpdateCategory(serverId, { hidden: false });
  return true;
}

export function getHiddenBuiltIn(): readonly BuiltInCategory[] {
  return BUILT_IN.filter((b) => settings.categoryOptions[b.id]?.hidden);
}

export function updateCategory(id: string, label: string, colorId: string): boolean {
  const isBuiltIn = BUILT_IN.some((b) => b.id === id);
  const serverId = serverCategoryIds[id];
  if (isBuiltIn) {
    // Store override for built-in
    settings = {
      ...settings,
      builtInOverrides: {
        ...settings.builtInOverrides,
        [id]: { label, colorId },
      },
    };
    persistSettings();
    emit();
    if (serverId) syncUpdateCategory(serverId, { label, colorId });
    return true;
  }
  const idx = customCategories.findIndex((c) => c.id === id);
  if (idx === -1) return false;
  customCategories = customCategories.map((c) =>
    c.id === id ? { id, label, colorId } : c,
  );
  persist(customCategories);
  emit();
  if (serverId) syncUpdateCategory(serverId, { label, colorId });
  return true;
}

export function getCustomCategories(): readonly CustomCategory[] {
  return customCategories;
}

export function setWorkHoursTitle(title: string) {
  settings = { ...settings, workHoursTitle: title };
  persistSettings();
  emit();
}

export function setCategoryOptions(catId: string, options: CategoryOptions) {
  settings = {
    ...settings,
    categoryOptions: { ...settings.categoryOptions, [catId]: options },
  };
  persistSettings();
  emit();
  const serverId = serverCategoryIds[catId];
  if (serverId) {
    syncUpdateCategory(serverId, {
      goalHours: options.goalHours,
      showInWorkHours: options.showInWorkHours,
      hidden: options.hidden ?? false,
    });
  }
}

export function getCategoryOptions(catId: string): CategoryOptions {
  return maps.categoryOptions[catId] ?? DEFAULT_OPTIONS;
}

// ---------------------------------------------------------------------------
// Quick accessors (non-reactive, for use outside React)
// ---------------------------------------------------------------------------

export function getCategoryLabel(cat: string): string {
  return maps.labels[cat] ?? cat;
}

export function getCategoryDotColor(cat: string): string {
  return maps.dotColors[cat] ?? "bg-gray-400";
}

export function getCategoryBarColor(cat: string): string {
  return maps.barColors[cat] ?? "bg-gray-400";
}

export function getCategoryBlockColor(cat: string): string {
  return maps.blockColors[cat] ?? "bg-gray-100 dark:bg-gray-500/20 border-l-gray-500 text-gray-700 dark:text-gray-200";
}

export function getCategoryBadgeColor(cat: string): string {
  return maps.badgeColors[cat] ?? "text-gray-700 bg-gray-50 dark:text-gray-300 dark:bg-gray-900/20";
}

export function getCategoryPillColor(cat: string): string {
  return maps.pillColors[cat] ?? "bg-gray-100 dark:bg-gray-500/20 text-gray-700 dark:text-gray-200";
}

export function getCategoryTagColor(cat: string): string {
  return maps.tagColors[cat] ?? "bg-gray-100 border-gray-300 text-gray-800";
}

export function getCategoryCssColor(cat: string): string {
  return maps.cssColors[cat] ?? "#9ca3af";
}

export function getAllCategories(): readonly string[] {
  return maps.categories;
}
